// Creatures, drawn small and soft: a body sprite per kind, then squash, hops and lunges done when drawing.
import { INK, inEll, sprite } from '../../legacy/art.gen.js';
import { Anim } from '../../../shared/sim.js';

type Px = (x: number, y: number) => string | null;
const make = (w: number, h: number, f: Px) => sprite(w, h, f) as HTMLCanvasElement;

const BODIES: Record<string, { img: HTMLCanvasElement; blink: HTMLCanvasElement; ax: number; ay: number }> = {};
function body(kind: string, w: number, h: number, f: (x: number, y: number, blink: boolean) => string | null, ax: number, ay: number) {
  BODIES[kind] = { img: make(w, h, (x, y) => f(x, y, false)), blink: make(w, h, (x, y) => f(x, y, true)), ax, ay };
}

// Puffball: a round, fluffy cream ball with rosy cheeks
body('puff', 16, 14, (x, y, b) => {
  if (!inEll(x, y, 8, 7.5, 7.2, 6.2)) return (y === 1 && (x === 6 || x === 9)) || (y === 0 && (x === 7 || x === 8)) ? '#f6e7f0' : null;
  if ((x === 5 || x === 10) && (y === 6 || y === 7)) return b ? (y === 7 ? INK : '#f6e7f0') : INK;
  if (y === 8 && (x === 3 || x === 4 || x === 11 || x === 12)) return '#f29bb8';
  if (y === 9 && (x === 7 || x === 8)) return '#b4473b';
  if (inEll(x, y, 6, 4.5, 3, 2.5)) return '#fffdf6';
  return y > 10 ? '#e3c7d6' : (x + y) % 5 === 0 ? '#efd8e4' : '#f6e7f0';
}, 8, 13);
// Meadow bunny: brown and white, long ears
body('bunny', 16, 18, (x, y, b) => {
  if ((x === 5 || x === 6 || x === 9 || x === 10) && y <= 6 && y >= 0) return (x === 6 || x === 9) && y >= 2 ? '#f6b7c4' : '#c9a07a';
  if (inEll(x, y, 8, 9.5, 5, 3.6)) {
    if ((x === 6 || x === 10) && y === 9) return b ? '#c9a07a' : INK;
    if (x === 8 && y === 11) return '#db614f';
    return inEll(x, y, 8, 11, 2.6, 1.6) ? '#fbf7ee' : '#c9a07a';
  }
  if (inEll(x, y, 8, 14.5, 5.6, 3.2)) return y > 16 ? '#a07a58' : inEll(x, y, 8, 15, 3, 1.8) ? '#fbf7ee' : '#c9a07a';
  if (inEll(x, y, 13.5, 14.5, 1.6, 1.6)) return '#fffdf6';
  return null;
}, 8, 17);
// Bramble beetle: a green shell with a seam, little legs
body('beetle', 18, 13, (x, y, b) => {
  if (y >= 9 && y <= 12 && (x === 3 || x === 7 || x === 10 || x === 14)) return '#2f3a24';
  if (inEll(x, y, 9, 3.5, 4, 2.6)) { if ((x === 7 || x === 11) && y === 3) return b ? '#3f5e2a' : '#fbf7ee'; return '#3f5e2a'; }
  if (inEll(x, y, 9, 7.5, 7.8, 4.6)) {
    if (x === 9 && y >= 4) return '#2f4720';
    if (inEll(x, y, 6, 6, 2, 1.2) || inEll(x, y, 12, 6, 2, 1.2)) return '#cbe4a6';
    return y > 10 ? '#4e7d36' : (x + y * 2) % 7 === 0 ? '#8fbf5a' : '#5e8a3a';
  }
  return null;
}, 9, 12);

export interface MobView { kind: string; x: number; y: number; dir: number; anim: number; t: number; hp: number; max: number; hurtAt: number; deadAt: number; id: number }

/** Draw one creature at its feet. `now` in ms. */
export function drawMob(ctx: CanvasRenderingContext2D, m: MobView, now: number): void {
  const b = BODIES[m.kind] ?? BODIES.puff;
  const phase = now / 1000 + (m.id % 17) * 0.37;
  let ox = 0, oy = 0, sx = 1, sy = 1, alpha = 1;
  if (m.anim === Anim.Walk || m.anim === Anim.Run) {
    const hop = Math.abs(Math.sin(phase * (m.anim === Anim.Run ? 14 : 9)));
    if (m.kind === 'beetle') oy = -Math.round(hop); else { oy = -Math.round(hop * (m.kind === 'bunny' ? 5 : 3)); sy = 1 + hop * 0.08; sx = 1 - hop * 0.06; }
  } else if (m.anim === Anim.Attack) {
    const k = Math.sin(Math.min(1, m.t / 12) * Math.PI);
    const [dx, dy] = [[0, 1], [1, 0], [0, -1], [-1, 0]][m.dir] ?? [0, 1];
    ox = Math.round(dx * k * 5); oy = Math.round(dy * k * 4 - k * 3); sx = 1 + k * 0.12; sy = 1 - k * 0.1;
  } else { const br = Math.sin(phase * 2.2); sy = 1 + br * 0.04; sx = 1 - br * 0.03; }
  if (m.deadAt) { const k = Math.min(1, (now - m.deadAt) / 500); alpha = 1 - k; sy *= 1 - k * 0.6; sx *= 1 + k * 0.3; }
  const x = Math.round(m.x), y = Math.round(m.y);
  // shadow stays on the ground
  ctx.globalAlpha = alpha * 0.9;
  ctx.fillStyle = 'rgba(47,42,38,.22)'; ctx.beginPath(); ctx.ellipse(x, y, b.img.width * 0.36, 2.2, 0, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = alpha;
  const img = (now + m.id * 211) % 3400 < 130 ? b.blink : b.img;
  const w = Math.round(b.img.width * sx), h = Math.round(b.img.height * sy), flip = m.dir === 3;
  ctx.save();
  ctx.translate(x + ox, y + oy);
  if (flip) ctx.scale(-1, 1);
  ctx.drawImage(img, -Math.round(b.ax * sx), -h + (b.img.height - b.ay - 1), w, h);
  if (now - m.hurtAt < 120) { ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = 0.6 * alpha; ctx.drawImage(img, -Math.round(b.ax * sx), -h + (b.img.height - b.ay - 1), w, h); }
  ctx.restore();
  ctx.globalAlpha = 1;
  // a little poof when it falls
  if (m.deadAt && now - m.deadAt < 600) {
    const k = (now - m.deadAt) / 600;
    for (let i = 0; i < 7; i++) { const a = i / 7 * Math.PI * 2; ctx.fillStyle = `rgba(251,247,238,${1 - k})`; ctx.fillRect(Math.round(x + Math.cos(a) * (4 + k * 12)), Math.round(y - 6 + Math.sin(a) * (3 + k * 8)), 2, 2); }
  }
}

/** Height of a creature's head above its feet (for bars and names). */
export const mobTop = (kind: string) => (BODIES[kind] ?? BODIES.puff).ay + 3;

/** A small health bar. */
export function drawHpBar(ctx: CanvasRenderingContext2D, x: number, y: number, hp: number, max: number, w = 16): void {
  const k = Math.max(0, Math.min(1, hp / Math.max(1, max))), X = Math.round(x - w / 2), Y = Math.round(y);
  ctx.fillStyle = INK; ctx.fillRect(X - 1, Y - 1, w + 2, 4);
  ctx.fillStyle = '#5a3a2a'; ctx.fillRect(X, Y, w, 2);
  ctx.fillStyle = k > 0.5 ? '#8fbf5a' : k > 0.25 ? '#f9c776' : '#ec5448'; ctx.fillRect(X, Y, Math.max(k > 0 ? 1 : 0, Math.round(w * k)), 2);
}
